import React, { useState } from "react";
import { Calendar, Info, Building2, Check, ChevronDown, X } from "lucide-react";
import ReusableDropdown, { unifiedControlStyle } from "../../../../../components/ReusableDropdown";
import ReusableMultiSelect from "../../../../../components/ReusableMultiSelect";

const MONTH_KEYS = [
  "january", "february", "march", "april", "may", "june",
  "july", "august", "september", "october", "november", "december",
];

// ─── helpers ────────────────────────────────────────────────────────────────

/** Returns every calendar date between two Date objects (inclusive) */
function datesBetween(start, end) {
  const dates = [];
  const cur = new Date(start);
  cur.setHours(0, 0, 0, 0);
  const endTs = new Date(end).setHours(0, 0, 0, 0);
  while (cur <= endTs) {
    dates.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

/** Short label shown in the dropdown list: "25 May" / "٢٥ مايو" */
function formatShortDate(date, language) {
  return date.toLocaleDateString(language === "ar" ? "ar-EG" : "en-US", {
    day: "numeric",
    month: "short",
  });
}

/** Long label for the banner: "25 May 2025" */
function formatLongDate(date, language) {
  return date.toLocaleDateString(language === "ar" ? "ar-EG" : "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/** Encode a Date as a stable string key "YYYY-MM-DD" */
function dateKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/** Derives everything needed to render the period UI for one company */
function computePeriodInfo({ companyConfig, period, language }) {
  const payrollStartDate = Number(companyConfig?.payrollStartDate) || 1;

  const selectedMonth = period.month;
  const selectedYear = period.year;

  // ── Cycle start month/year, mirroring autoPayrollGenerator.js's
  //    computeConfigDates exactly:
  //      >= 15: cycle = (previous month, day N)  →  (this month, day N-1)
  //      <  15: cycle = (this month, day N)      →  (next month, day N-1)
  const { cycleStartYear, cycleStartMonth } = (() => {
    if (payrollStartDate >= 15) {
      let m = selectedMonth - 1;
      let y = selectedYear;
      if (m < 1) { m = 12; y -= 1; }
      return { cycleStartYear: y, cycleStartMonth: m };
    }
    return { cycleStartYear: selectedYear, cycleStartMonth: selectedMonth };
  })();

  const cycleStartDate = new Date(cycleStartYear, cycleStartMonth - 1, payrollStartDate);

  // ── "Anchor" month the end date's day-offset is measured from:
  //      >= 15: anchor is THIS month
  //      <  15: anchor is NEXT month
  //    We pass (payrollStartDate - 1) straight into Date() as the day and let
  //    JS's native rollover handle it. That's what fixes payrollStartDate===1:
  //    day 0 of the anchor month automatically becomes the LAST day of the
  //    month before it (the same month the cycle started in) — previously the
  //    code special-cased this to "last day of the anchor month" instead,
  //    which is one month too late.
  const { anchorEndYear, anchorEndMonth } = (() => {
    if (payrollStartDate >= 15) {
      return { anchorEndYear: selectedYear, anchorEndMonth: selectedMonth };
    }
    let m = selectedMonth + 1;
    let y = selectedYear;
    if (m > 12) { m = 1; y += 1; }
    return { anchorEndYear: y, anchorEndMonth: m };
  })();

  const naturalEndDate = new Date(anchorEndYear, anchorEndMonth - 1, payrollStartDate - 1);

  const lastDayOfAnchorMonth = new Date(anchorEndYear, anchorEndMonth, 0).getDate();
  const defaultEndDate = companyConfig?.cutoffDay
    ? new Date(anchorEndYear, anchorEndMonth - 1, Math.min(companyConfig.cutoffDay, lastDayOfAnchorMonth))
    : naturalEndDate;

  const defaultEndKey = dateKey(defaultEndDate);

  const selectedEndKey = period.endDate ?? defaultEndKey;
  const selectedEndDate = (() => {
    if (!period.endDate) return defaultEndDate;
    const [y, m, d] = period.endDate.split("-").map(Number);
    return new Date(y, m - 1, d);
  })();

  const isConfigDefault = selectedEndKey === defaultEndKey;

  const allDatesInRange = datesBetween(cycleStartDate, defaultEndDate);

  const dateItems = allDatesInRange.map((d) => {
    const key = dateKey(d);
    const isDefault = key === defaultEndKey;
    const shortLabel = formatShortDate(d, language);
    return {
      id: key,
      date: d,
      label: isDefault
        ? `${shortLabel}  ✦ ${language === "ar" ? "افتراضي" : "default"}`
        : shortLabel,
    };
  });

  return {
    cycleStartDate,
    defaultEndDate,
    defaultEndKey,
    selectedEndDate,
    selectedEndKey,
    isConfigDefault,
    dateItems,
    allDatesInRange,
  };
}



// ─── per-company period fields ──────────────────────────────────────────────

const CompanyPeriodFields = ({
  company,
  companyConfig,
  period,
  onPeriodChange,
  modalStyles,
  currentTheme,
  t,
  theme,
  language,
  showCompanyLabel,
}) => {
  const months = MONTH_KEYS.map((key, idx) => ({ id: idx + 1, label: t(key) }));
  const isDark = theme === "dark";
  const c = currentTheme.colors;
  const controlStyle = {
    ...unifiedControlStyle(theme, language === "ar"),
    padding: "13px 16px",
    borderRadius: "10px",
    border: `1px solid ${c.border}`,
    background: c.inputBackground,
    color: c.textPrimary,
    fontSize: "14.5px",
  };

  const {
    cycleStartDate,
    defaultEndDate,
    defaultEndKey,
    selectedEndDate,
    selectedEndKey,
    isConfigDefault,
    dateItems,
    allDatesInRange,
  } = computePeriodInfo({ companyConfig, period, language });

  const cycleLabel =
    language === "ar"
      ? `احتساب الراتب من ${formatLongDate(cycleStartDate, language)} إلى ${formatLongDate(selectedEndDate, language)}`
      : `Payroll cycle: ${formatLongDate(cycleStartDate, language)} → ${formatLongDate(selectedEndDate, language)}`;

  const overrideLabel =
    language === "ar"
      ? `⚙️ اليوم الافتراضي من الإعدادات: ${formatShortDate(defaultEndDate, language)}`
      : `⚙️ Config default: ${formatShortDate(defaultEndDate, language)}`;

  const cycleBannerStyle = {
    fontSize: "13px",
    fontWeight: 500,
    marginTop: "4px",
    padding: "10px 12px",
    lineHeight: 1.45,
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    transition: "all 0.2s ease",
    ...(isConfigDefault
      ? {
        color: isDark ? "#60a5fa" : "#2563eb",
        background: isDark ? "rgba(59,130,246,0.12)" : "rgba(37,99,235,0.07)",
        border: `1px solid ${isDark ? "rgba(96,165,250,0.3)" : "rgba(37,99,235,0.2)"}`,
      }
      : {
        color: isDark ? "#f59e0b" : "#b45309",
        background: isDark ? "rgba(245,158,11,0.10)" : "rgba(180,83,9,0.06)",
        border: `1px solid ${isDark ? "rgba(245,158,11,0.3)" : "rgba(180,83,9,0.2)"}`,
      }),
  };

  const configHintStyle = {
    fontSize: "12px",
    marginTop: "2px",
    padding: "6px 10px",
    borderRadius: "6px",
    color: isDark ? "rgba(203,213,225,0.5)" : "rgba(100,116,139,0.75)",
    background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"}`,
    display: "flex",
    alignItems: "center",
    gap: "4px",
  };

  const cardStyle = showCompanyLabel
    ? {
      border: `1px solid ${c.borderLight}`,
      borderRadius: "12px",
      padding: "20px",
      marginTop: "16px",
      background: c.surfaceSubtle,
    }
    : {};

  const handleEndDateSelect = (item) => {
    onPeriodChange({ ...period, endDate: item.id });
  };

  const handleEndDateClear = () => {
    onPeriodChange({ ...period, endDate: defaultEndKey });
  };

  return (
    <div style={cardStyle}>
      {showCompanyLabel && (
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          fontSize: "15px",
          fontWeight: 700,
          marginBottom: "16px",
          color: c.textPrimary,
        }}>
          <Building2 size={17} style={{ color: c.primary }} />
          {company.name}
        </div>
      )}

      <div style={modalStyles.formGrid}>
        {/* ── Month ──────────────────────────────────────────────────────── */}
        <div style={modalStyles.formGroup}>
          <label style={modalStyles.label}>{t("month")}</label>
          <ReusableDropdown
            items={months}
            value={period.month}
            onChange={(val) => {
              if (val) onPeriodChange({ ...period, month: val, endDate: undefined });
            }}
            onSelect={(item) => onPeriodChange({ ...period, month: item.id, endDate: undefined })}
            placeholder={t("selectMonth") || "Select Month"}
            getItemKey={(item) => item.id}
            getItemLabel={(item) => item.label}
            theme={theme}
            language={language}
            inputStyle={controlStyle}
          />
        </div>

        {/* ── Year ───────────────────────────────────────────────────────── */}
        <div style={modalStyles.formGroup}>
          <label style={modalStyles.label}>{t("year")}</label>
          <ReusableDropdown
            items={[2023, 2024, 2025, 2026].map((year) => ({ id: year, label: String(year) }))}
            value={period.year}
            onChange={(val) => onPeriodChange({ ...period, year: val, endDate: undefined })}
            onSelect={(item) => onPeriodChange({ ...period, year: item.id, endDate: undefined })}
            placeholder={t("selectYear") || "Select Year"}
            getItemKey={(item) => item.id}
            getItemLabel={(item) => item.label}
            theme={theme}
            language={language}
            inputStyle={controlStyle}
          />
        </div>

        {/* ── Cycle end date ─────────────────────────────────────────────── */}
        <div style={modalStyles.formGroup}>
          <label style={modalStyles.label}>
            {language === "ar" ? "تاريخ نهاية الدورة" : "Cycle end date"}
          </label>

          <ReusableDropdown
            items={dateItems}
            value={selectedEndKey}
            onChange={() => { }}
            onSelect={handleEndDateSelect}
            onClear={handleEndDateClear}
            placeholder={formatShortDate(defaultEndDate, language)}
            getItemKey={(item) => item.id}
            getItemLabel={(item) => item.label}
            theme={theme}
            language={language}
            maxItems={allDatesInRange.length}
            showAllOnArrowClick={true}
            showClear={!isConfigDefault}
            inputStyle={controlStyle}
          />

          <div style={cycleBannerStyle}>
            <Calendar size={13} style={{ flexShrink: 0 }} />
            {cycleLabel}
          </div>

          {!isConfigDefault && (
            <div style={configHintStyle}>
              <Info size={11} style={{ flexShrink: 0 }} />
              {overrideLabel}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── main component ─────────────────────────────────────────────────────────

const StepSelectPeriod = ({
  formData,
  setFormData,
  handleInputChange,
  modalStyles,
  currentTheme,
  t,
  theme,
  language,
  companies = [],
  companyConfig,
  companyConfigsById = {},
  companiesLoading = false,
  companiesError = null,
}) => {
  const selectedCompanyIds = formData.companyIds || [];
  const now = new Date();
  const c = currentTheme.colors;
  const L = (ar, en) => (language === "ar" ? ar : en);

  const handleCompanySelectionChange = (newIds) => {
    setFormData((prev) => {
      // Keep only periods for currently selected companies
      const nextPeriods = { ...prev.periodsByCompany };
      Object.keys(nextPeriods).forEach(id => {
        if (!newIds.includes(id)) {
          delete nextPeriods[id];
        }
      });
      return {
        ...prev,
        companyIds: newIds,
        periodsByCompany: nextPeriods,
      };
    });
  };

  const selectedCompanies = companies.filter((company) => selectedCompanyIds.includes(company.id));
  const stateMessage = { fontSize: "14px", color: c.textMuted, padding: "8px 0" };

  return (
    <div className="pp-section" style={{ ...modalStyles.formSection, marginBottom: 0 }}>
      <div style={{ marginBottom: "24px" }}>
        <h3 style={modalStyles.sectionTitle}>
          <Calendar size={20} style={{ color: c.primary }} />
          {t("payPeriod")}
        </h3>
        <p style={modalStyles.sectionSubtitle}>
          {L(
            "اختر الشركة (أو أكثر) ثم حدد الشهر والسنة ونهاية دورة الرواتب لكل شركة.",
            "Pick one or more companies, then set the month, year and cycle end date for each.",
          )}
        </p>
      </div>

      {/* ── Company selector ────────────────────────────────────────────── */}
      <div style={modalStyles.formGroup}>
        <label style={modalStyles.label}>
          {t("company") || L("الشركة", "Company")}
        </label>

        {companiesLoading ? (
          <div style={stateMessage}>{L("جارٍ تحميل الشركات...", "Loading companies...")}</div>
        ) : companiesError ? (
          <div style={{ ...stateMessage, color: c.danger }}>
            {L("تعذر تحميل قائمة الشركات", "Failed to load companies")}
          </div>
        ) : companies.length === 0 ? (
          <div style={stateMessage}>
            {L("لا توجد شركات متاحة لهذا العميل", "No companies found for this client")}
          </div>
        ) : (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: "12px",
          }}>
            {companies.map(company => {
              const isSelected = selectedCompanyIds.includes(company.id);
              return (
                <button
                  key={company.id}
                  type="button"
                  className="pp-chip"
                  aria-pressed={isSelected}
                  onClick={() => {
                    const newIds = isSelected
                      ? selectedCompanyIds.filter(id => id !== company.id)
                      : [...selectedCompanyIds, company.id];
                    handleCompanySelectionChange(newIds);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "14px 16px",
                    borderRadius: "12px",
                    border: `1px solid ${isSelected ? c.primary : c.border}`,
                    boxShadow: isSelected ? `0 0 0 3px ${c.primarySoft}` : "none",
                    background: isSelected ? c.primarySoft : c.surface,
                    color: c.textPrimary,
                    fontSize: "14.5px",
                    fontWeight: 600,
                    cursor: "pointer",
                    textAlign: "start",
                    transition: "all 0.15s ease",
                  }}
                >
                  <span style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "10px",
                    background: isSelected ? c.primary : c.surfaceSubtle,
                    color: isSelected ? "#ffffff" : c.textMuted,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    <Building2 size={18} />
                  </span>
                  <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {company.name}
                  </span>
                  <span style={{
                    width: "20px",
                    height: "20px",
                    borderRadius: "6px",
                    border: `2px solid ${isSelected ? c.primary : c.border}`,
                    background: isSelected ? c.primary : "transparent",
                    color: "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    {isSelected && <Check size={13} strokeWidth={3} />}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Per-Company Period selectors ──────────────────────────────────────────── */}
      {selectedCompanies.length > 0 && (
        <div style={{ marginTop: "8px" }}>
          {selectedCompanies.map(company => {
            const defaultPeriod = { month: now.getMonth() + 1, year: now.getFullYear(), endDate: undefined };
            const period = (formData.periodsByCompany && formData.periodsByCompany[company.id]) || defaultPeriod;
            return (
              <CompanyPeriodFields
                key={company.id}
                company={company}
                companyConfig={companyConfigsById[company.id] || companyConfig}
                period={period}
                onPeriodChange={(next) => {
                  setFormData(prev => ({
                    ...prev,
                    periodsByCompany: {
                      ...(prev.periodsByCompany || {}),
                      [company.id]: next
                    }
                  }));
                }}
                modalStyles={modalStyles}
                currentTheme={currentTheme}
                t={t}
                theme={theme}
                language={language}
                showCompanyLabel={true}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StepSelectPeriod;
